import {
  Activity,
  Calendar,
  Calculator,
  Clock,
  Droplets,
  Ear,
  Eye,
  Heart,
  Ruler,
  Scale,
  Thermometer,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import { VITAL_CODES, type VitalCodeValue } from './vital-codes';

/**
 * UI catalog — one entry per vital code rendered in the Vitals tab.
 * Mirrors Flutter `emptyVitalUiList(gender, age)` in
 * `ui_data.dart:1184-1368`. The Flutter list defines display label,
 * unit, "normal value", icon, and gating rules; we copy those values
 * verbatim so the React render visually matches Flutter.
 *
 * Order matters — this is the render order in the tab. Matches Flutter
 * line-for-line so the grid layout is comparable side-by-side during
 * the Playwright verification step.
 *
 * Icons: Flutter ships custom `ScripsIcons.heartRate` / `.bloodPressure`
 * / `.height` etc. The React icon-font atom doesn't have those
 * codepoints yet (only the row-action set: cancel, noShow, walking,
 * etc.). Using lucide approximations for V1 — TODO add the vital
 * glyphs to `scrips-icon.tsx` so the visual matches pixel-perfect.
 */

export interface VitalCatalogEntry {
  /** LOINC (or sentinel) code. */
  code: VitalCodeValue;
  /** Display label rendered in the cell. */
  label: string;
  /** Short label for narrow viewports. Falls back to `label`. */
  shortLabel?: string;
  /** Display unit suffix (e.g. "°C", "kg", "BPM"). */
  unit: string;
  /** Reference "normal" value — drives the color hint. Some vitals
   *  have no canonical normal (Height / Weight / LMP). */
  normalValue?: number | null;
  /** Secondary normal value for combo cells (BP diastolic). */
  normalValueSecond?: number;
  /** Backend computes this — UI is read-only. */
  isComputed?: boolean;
  /** Render-only flag — combo cells display both their value and the
   *  partner code's value (BP = systolic / diastolic). */
  combinesWith?: VitalCodeValue;
  /** Lucide icon — see header note about Scrips-glyph migration. */
  icon: LucideIcon;
  /** Gating rule. `always` renders for every patient; `femaleOver8` is
   *  the LMP row, `under2` is HCM. */
  gating: 'always' | 'femaleOver8' | 'under2';
  /** Max characters in the numeric input (PR 2 — kept here so PR 1
   *  read-only render and PR 2 edit mode share one source of truth). */
  maxDigits?: number;
  /**
   * Edit-mode behavior (PR 2):
   *   - `'numeric'`     — single text input (decimal allowed).
   *   - `'combo'`       — two inputs side-by-side (BP only — reads
   *                       `combinesWith` for the partner code).
   *   - `'computed'`    — no input. Value is server-computed (BMI,
   *                       waist-to-hip ratio). Render the same way in
   *                       both modes.
   *   - `'lmp'`         — date picker + isMenstrual toggle. PR 3a.
   *   - `'hearing'`     — two L/R toggle groups (SL / L / G). PR 3a.
   *   - `'visual'`      — OS / OD numerator + denominator dropdowns.
   *                       Simplified inline editor; the full-grid
   *                       multi-mode (distance/near × eye × sc/cc/ph)
   *                       lives in the PA encounter screen, not here.
   *                       PR 3b.
   */
  editor:
    | 'numeric'
    | 'combo'
    | 'computed'
    | 'lmp'
    | 'hearing'
    | 'visual';
  /** Allow negative numbers (Bioage). Default false. */
  allowNegative?: boolean;
  /** Allow a decimal point in the input (Temperature, Height, Weight,
   *  HCM, hip, waist). Defaults to true for `'numeric'`/`'combo'`. */
  allowDecimal?: boolean;
  /**
   * Render a small sparkline trend chart in the cell from the patient's
   * vital history. Flutter parity: `VitalUiData.showGraph` in
   * `ui_data.dart:1184-1368`. Default `true`. Set `false` for vitals
   * without meaningful temporal trends (circumferences, ratios, visual,
   * hearing, LMP, bioage).
   */
  showGraph?: boolean;
}

export const VITAL_CATALOG: readonly VitalCatalogEntry[] = [
  {
    code: VITAL_CODES.TEMPERATURE,
    label: 'Temperature',
    shortLabel: 'Temp',
    unit: '°C',
    normalValue: 36,
    icon: Thermometer,
    gating: 'always',
    maxDigits: 4,
    editor: 'numeric',
  },
  {
    code: VITAL_CODES.HEART_RATE,
    label: 'Pulse',
    unit: 'BPM',
    normalValue: 80,
    icon: Heart,
    gating: 'always',
    maxDigits: 3,
    editor: 'numeric',
    allowDecimal: false,
  },
  {
    code: VITAL_CODES.SYSTOLIC,
    label: 'BP',
    unit: 'mmHg',
    normalValue: 120,
    normalValueSecond: 70,
    combinesWith: VITAL_CODES.DIASTOLIC,
    icon: Activity,
    gating: 'always',
    maxDigits: 3,
    editor: 'combo',
    allowDecimal: false,
  },
  {
    code: VITAL_CODES.RESP_RATE,
    label: 'Resp Rate',
    unit: 'RPM',
    normalValue: 14,
    icon: Wind,
    gating: 'always',
    editor: 'numeric',
    allowDecimal: false,
  },
  {
    code: VITAL_CODES.O2_SAT,
    label: 'O₂ Sat',
    unit: '%',
    normalValue: 93,
    icon: Droplets,
    gating: 'always',
    maxDigits: 3,
    editor: 'numeric',
    allowDecimal: false,
  },
  {
    code: VITAL_CODES.HEIGHT,
    label: 'Height',
    unit: 'cm',
    normalValue: null,
    icon: Ruler,
    gating: 'always',
    maxDigits: 5,
    editor: 'numeric',
  },
  {
    code: VITAL_CODES.WEIGHT,
    label: 'Weight',
    unit: 'kg',
    normalValue: null,
    icon: Scale,
    gating: 'always',
    maxDigits: 5,
    editor: 'numeric',
  },
  {
    code: VITAL_CODES.BMI,
    label: 'BMI',
    unit: '',
    normalValue: null,
    isComputed: true,
    icon: Calculator,
    gating: 'always',
    editor: 'computed',
  },
  {
    code: VITAL_CODES.HIP,
    label: 'Hip Circumference',
    shortLabel: 'Hip',
    unit: 'cm',
    normalValue: null,
    icon: Ruler,
    gating: 'always',
    maxDigits: 5,
    editor: 'numeric',
    showGraph: false,
  },
  {
    code: VITAL_CODES.WAIST,
    label: 'Waist Circumference',
    shortLabel: 'Waist',
    unit: 'cm',
    normalValue: null,
    icon: Ruler,
    gating: 'always',
    maxDigits: 5,
    editor: 'numeric',
    showGraph: false,
  },
  {
    code: VITAL_CODES.WAIST_TO_HIP_RATIO,
    label: 'Waist-to-Hip Ratio',
    shortLabel: 'W/H',
    unit: '',
    normalValue: 0.8,
    isComputed: true,
    icon: Calculator,
    gating: 'always',
    editor: 'computed',
    showGraph: false,
  },
  {
    code: VITAL_CODES.BIOAGE,
    label: 'Bioage',
    unit: '',
    normalValue: null,
    icon: Clock,
    gating: 'always',
    maxDigits: 3,
    editor: 'numeric',
    allowNegative: true,
    allowDecimal: false,
    showGraph: false,
  },
  {
    code: VITAL_CODES.VISUAL,
    label: 'Visual Acuity',
    shortLabel: 'Visual',
    unit: '',
    normalValue: null,
    icon: Eye,
    gating: 'always',
    maxDigits: 3,
    editor: 'visual',
    showGraph: false,
  },
  {
    code: VITAL_CODES.HEARING,
    label: 'Hearing Test',
    shortLabel: 'Hearing',
    unit: '',
    normalValue: null,
    icon: Ear,
    gating: 'always',
    editor: 'hearing',
    showGraph: false,
  },
  {
    code: VITAL_CODES.LMP,
    label: 'LMP',
    unit: '',
    normalValue: null,
    icon: Calendar,
    gating: 'femaleOver8',
    editor: 'lmp',
    showGraph: false,
  },
  {
    code: VITAL_CODES.HCM,
    label: 'Head Circumference',
    shortLabel: 'HCM',
    unit: 'cm',
    normalValue: null,
    icon: Ruler,
    gating: 'under2',
    maxDigits: 5,
    editor: 'numeric',
  },
];

/**
 * Filters the catalog by patient gender + age. Matches Flutter
 * `emptyVitalUiList(gender, age)`:
 *   - LMP only for `gender === 'female' && age > 8`
 *   - HCM only for `age <= 2`
 * Anything else returns the always-on rows.
 *
 * `age` is in years; pass `null` if unknown and we'll be conservative
 * (no LMP / no HCM).
 */
export function filterCatalogForPatient(
  gender: 'male' | 'female' | null | undefined,
  age: number | null | undefined,
): VitalCatalogEntry[] {
  return VITAL_CATALOG.filter((entry) => {
    if (entry.gating === 'always') return true;
    if (age == null) return false;
    if (entry.gating === 'femaleOver8') return gender === 'female' && age > 8;
    if (entry.gating === 'under2') return age <= 2;
    return false;
  });
}
